import React from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Share2, Sparkles, Star } from "lucide-react";
import { CopyContentButton } from "@/components/ai-assistant/CopyContentButton";
import { OutputHeader } from "@/components/ai-assistant/OutputHeader";
import { AIContentRenderer } from "@/components/ai-assistant/AIContentRenderer";
import { EditableAIContentRenderer } from "@/components/ai-assistant/EditableAIContentRenderer";
import { ShareOutputDialog } from "@/components/ai-assistant/ShareOutputDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface AssistantGeneratedPanelProps {
  organizationBrandColor?: string | null;
  formFields: any[];
  formData: Record<string, any>;
  generatedContent: any;
  isGenerating: boolean;
  currentOutputId: string | null;
  isCurrentOutputSaved: boolean;
  renderingSchema?: unknown;
  onClickImprove: () => void;
  onClickSave: () => void;
  onTitleSave: (title: string) => Promise<void> | void;
  onSaveGeneratedContent: (
    newContent: string | Record<string, any>,
  ) => Promise<void> | void;
  generationError?: string | null;
  extraActionsRight?: React.ReactNode;
  // Persist edit mode after refresh when DB flag is set
  alwaysEdit?: boolean;
}

export function AssistantGeneratedPanel(props: AssistantGeneratedPanelProps) {
  const {
    organizationBrandColor,
    formFields,
    formData,
    generatedContent,
    isGenerating,
    currentOutputId,
    isCurrentOutputSaved,
    renderingSchema,
    onClickImprove,
    onClickSave,
    onTitleSave,
    onSaveGeneratedContent,
    generationError,
    extraActionsRight,
    alwaysEdit,
  } = props;

  return (
    <div className="flex-1 bg-gray-50 overflow-y-auto">
      <div className="p-6">
        <div className="max-w-[750px] mx-auto">
          <div className="mb-2 flex items-center justify-between">
            <div />
            {generatedContent && !isGenerating && (
              <div className="flex gap-2">
                {extraActionsRight}
                <Button
                  size="sm"
                  onClick={onClickImprove}
                  className="flex items-center gap-2 bg-white hover:bg-gray-50 text-[rgb(158,172,211)] border border-[rgb(158,172,211)] hover:border-[rgb(140,156,195)] hover:text-[rgb(140,156,195)]"
                >
                  <Sparkles className="h-4 w-4" />
                  Improve
                </Button>
                <CopyContentButton
                  content={generatedContent}
                  title="AI Generated Content"
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                />
                {currentOutputId && (
                  <ShareOutputDialog outputId={currentOutputId}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Share2 className="h-4 w-4" />
                      Share
                    </Button>
                  </ShareOutputDialog>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClickSave}
                  disabled={!currentOutputId}
                  className="h-8 w-8"
                  title={isCurrentOutputSaved ? "Unstar" : "Star"}
                >
                  <Star
                    className={`h-4 w-4 ${
                      isCurrentOutputSaved ? "text-yellow-500" : ""
                    }`}
                    fill={isCurrentOutputSaved ? "currentColor" : "none"}
                  />
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <OutputHeader
              title={
                (generatedContent as any)?.output_title || "Generated Content"
              }
              formFields={formFields}
              formData={formData}
              className="flex-1"
              canEditTitle={!!currentOutputId}
              onTitleSave={onTitleSave}
              belowTitleSlot={
                alwaysEdit ? (
                  <div className="relative">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                      edit mode
                    </span>
                    <button
                      className="absolute -top-1 right-0 h-7 w-7 inline-flex items-center justify-center rounded bg-white border text-gray-600 hover:bg-gray-50"
                      title="Full screen"
                      onClick={() => {
                        const ev = new CustomEvent("editor-enter-fullscreen", {
                          detail: { persistKey: currentOutputId },
                        });
                        window.dispatchEvent(ev);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" y1="3" x2="14" y2="10"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                      </svg>
                    </button>
                  </div>
                ) : null
              }
            />
          </div>

          {isGenerating ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-yellow-600">
                <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">
                  Generating content...
                </span>
              </div>
              {generatedContent && (
                <div className="border-2 border-dashed border-yellow-200 rounded-lg p-4">
                  <AIContentRenderer
                    content={generatedContent}
                    brandColor={organizationBrandColor || undefined}
                    schema={renderingSchema as any}
                  />
                  <div className="flex items-center gap-2 text-sm text-yellow-600 pt-3 border-t border-yellow-200 mt-3">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Content is being generated in real-time...</span>
                  </div>
                </div>
              )}
            </div>
          ) : generatedContent ? (
            <div className="space-y-2">
              <EditableAIContentRenderer
                key={currentOutputId || "generated"}
                content={
                  typeof generatedContent === "string"
                    ? generatedContent
                    : undefined
                }
                onSave={async (newContent) => {
                  console.log(
                    "🧩 Panel onSave called for output:",
                    currentOutputId,
                  );
                  await onSaveGeneratedContent(newContent);
                }}
                canEdit={!!currentOutputId}
                persistKey={currentOutputId || undefined}
                schema={renderingSchema as any}
                brandColor={organizationBrandColor || undefined}
                alwaysEdit={Boolean(alwaysEdit)}
                onEnterEdit={async () => {
                  try {
                    if (!currentOutputId) return;
                    console.log(
                      "✳️ Persisting edit mode in DB for:",
                      currentOutputId,
                    );
                    const svc = (await import("@/services/aiAssistantService"))
                      .aiAssistantService;
                    await svc.setOutputEditMode(currentOutputId, true);
                  } catch (e) {
                    console.warn("Failed to persist edit mode:", e);
                  }
                }}
                initialJson={
                  typeof generatedContent === "object" &&
                  generatedContent !== null
                    ? (generatedContent as any)
                    : undefined
                }
              />
            </div>
          ) : null}

          {generationError && (
            <Alert className="mt-4">
              <AlertDescription className="text-red-600">
                {generationError}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
